import "./ChartRangeSelectorComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {Observable, ReplaySubject, Subject} from "rxjs";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {SimulationDates} from "../../models/SimulationDates";
import {TradeSimulatorViewStore} from "../../service/TradeSimulatorViewStore";
import {DateSelectorEvent} from "./models/DateSelectorEvent";
import {Expiries} from "../../../services/expiry/Expiries";
import {HistoricalPriceRange} from "../../../services/price/historical/HistoricalPriceModels";
import {distinct} from "../../../js/util/UtilFunctions";
import {IsoDate, IsoDateString} from "../../../pseudo-types/DateTypes";
import {Optional} from "../../../js/util/Optional";

export const ChartRange = "tfSimulationChartRange";

export function chartRange(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/chart-range-selector/ChartRangeSelectorComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: ChartRangeSelectorComponentController
    };
}

export class ChartRangeSelectorComponentController implements IOnInit, IOnDestroy {

    public viewStore: TradeSimulatorViewStore;

    public selectedDate: Date;
    public minSelectableDate: Date;
    public maxSelectableDate: Date;

    public hasPreviousExpiry = false;
    public hasPreviousDay = false;
    public hasNextDay = false;
    public hasNextExpiry = false;

    public jumpPoints$: Observable<Array<DateSelectorEvent>>;

    private previousExpiry$ = new Subject<void>();
    private previousDay$ = new Subject<void>();
    private nextDay$ = new Subject<void>();
    private nextExpiry$ = new Subject<void>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope) {

    }

    public $onInit(): void {

        this.jumpPoints$ = this.initJumpPoints();
        const allExpiries$ = this.initExpiryList();

        Observable.combineLatest(this.viewStore.currentDate, this.viewStore.priceRange, allExpiries$)
                  .takeUntil(this.destroyed$)
                  .do((args) => console.debug(`Updating Date Selector Display: `, ...args))
                  .subscribe(([currentDate, priceRange, expiries]) => {
                      this.updateDisplay(currentDate, priceRange, expiries);
                  });

        this.viewStore.priceRange
            .takeUntil(this.destroyed$)
            .subscribe(this.configureBounds.bind(this));

        this.initButtonObservers(this.jumpPoints$, allExpiries$);
    }

    private initJumpPoints(): Observable<Array<DateSelectorEvent>> {
        return Observable.combineLatest(this.viewStore.simulationExpiries, this.viewStore.trades)
                         .takeUntil(this.destroyed$)
                         .do((args) => console.debug(`Calculating new Jump points`, ...args))
                         .map(([expiries, trades]) => {
                             const tradeDates = trades.map((tr) => tr.date);
                             const allDates = Array.from(expiries)
                                                   .concat(tradeDates)
                                                   .sort()
                                                   .filter(distinct);
                             return allDates.map((date) => {
                                 const eventDate = IsoDateString(date);
                                 const isExpiry = expiries.includes(date);
                                 const isTradeDate = tradeDates.includes(date);
                                 return DateSelectorEvent.create(eventDate, isExpiry, isTradeDate);
                             });
                         })
                         .do((events) => console.debug(`Jump Points Calculated: `, events))
                         .shareReplay(1);
    }

    private initExpiryList(): Observable<Expiries> {
        return Observable.combineLatest(this.viewStore.expiries, this.viewStore.currentSimulation)
                         .takeUntil(this.destroyed$)
                         .do((args) => console.debug(`Creating Expiry List: `, ...args))
                         .map(([expiries, simulation]) => {
                             if (simulation == null) {
                                 return expiries;
                             } else {
                                 return expiries.ofType("MONTHLY")
                                                .forUnderlying(simulation.underlying);
                             }
                         })
                         .do((expiries) => console.debug(`Created Expiry List: `, expiries))
                         .shareReplay(1);
    }

    /* Each of the buttons triggers an observable to perform the given action */
    private initButtonObservers(jumpPoints$: Observable<Array<DateSelectorEvent>>, allExpiries$: Observable<Expiries>) {

        const currentExpiriesEvents$ = Observable.combineLatest(this.viewStore.currentDate, allExpiries$, jumpPoints$);

        this.previousExpiry$
            .takeUntil(this.destroyed$)
            .withLatestFrom(currentExpiriesEvents$)
            .map(([_, data]) => data)
            .filter(([current, _]) => current != null)
            .subscribe(([current, expiries, events]) => this.selectPreviousExpiry(current!, expiries, events));

        this.nextExpiry$
            .takeUntil(this.destroyed$)
            .withLatestFrom(currentExpiriesEvents$)
            .map(([_, data]) => data)
            .filter(([current, _]) => current != null)
            .subscribe(([current, expiries, events]) => this.selectNextExpiry(current!, expiries, events));

        const currentAndRange$ = Observable.combineLatest(this.viewStore.currentDate, this.viewStore.dateRange);

        this.previousDay$
            .takeUntil(this.destroyed$)
            .withLatestFrom(currentAndRange$)
            .map(([_, data]) => data)
            .filter(([current, _]) => current != null)
            .subscribe(([current, range]) => this.selectPreviousDay(current!, range));

        this.nextDay$
            .takeUntil(this.destroyed$)
            .withLatestFrom(currentAndRange$)
            .map(([_, data]) => data)
            .filter(([current, _]) => current != null)
            .subscribe(([current, range]) => this.selectNextAvailableDay(current!, range));
    }

    private updateDisplay(selectedDate: string | null, range: HistoricalPriceRange, expiries: Expiries) {
        if (selectedDate == null) {
            this.hasPreviousExpiry = false;
            this.hasPreviousDay = false;
            this.hasNextDay = false;
            this.hasNextExpiry = false;
        } else {
            const firstExpiry = expiries.first;
            const lastExpiry = expiries.last;
            this.selectedDate = DateTimeUtil.createDate(selectedDate);
            this.hasPreviousExpiry = selectedDate.localeCompare(firstExpiry) > 0;
            this.hasPreviousDay = selectedDate !== range.from;
            this.hasNextDay = selectedDate !== range.to;
            this.hasNextExpiry = selectedDate.localeCompare(lastExpiry) < 0;
        }
        this.$scope.$applyAsync();
    }

    private configureBounds(range: HistoricalPriceRange) {
        this.minSelectableDate = DateTimeUtil.createDate(range.from);
        this.maxSelectableDate = DateTimeUtil.createDate(range.to);
    }

    public previousExpiry() {
        this.previousExpiry$.next();
    }

    private selectPreviousExpiry(current: string, expiries: Expiries, jumpPoints: Array<DateSelectorEvent>) {
        const expiryDates = expiries.expiryDates().map(IsoDateString);
        const dates = jumpPoints.map((p) => p.date).concat(expiryDates).sort();

        const beforeTarget = dates.filter((d) => d.localeCompare(current) < 0);
        const closest = beforeTarget.sort().reverse()[0];
        Optional.of(closest).map((d) => IsoDateString(d))
                .ifPresent((d) => this.changeSelection(d));
    }

    public previousDay() {
        this.previousDay$.next();
    }

    private selectPreviousDay(current: string, range: SimulationDates) {
        range.previous(current)
             .ifPresent(this.changeSelection.bind(this));
    }

    public nextDay() {
        this.nextDay$.next();
    }

    private selectNextAvailableDay(current: string, range: SimulationDates) {
        range.next(current)
             .ifPresent(this.changeSelection.bind(this));
    }

    public nextExpiry() {
        this.nextExpiry$.next();
    }

    private selectNextExpiry(current: string, expiries: Expiries, jumpPoints: Array<DateSelectorEvent>) {
        const expiryDates = expiries.expiryDates().map(IsoDateString);
        const dates = jumpPoints.map((p) => p.date).concat(expiryDates).sort();

        const afterTarget = dates.filter((d) => d.localeCompare(current) > 0);
        const closest = afterTarget.sort()[0];
        Optional.of(closest)
                .map((d) => IsoDateString(d))
                .ifPresent((d) => this.changeSelection(d));
    }

    public selectDate(date: IsoDate) {
        this.changeSelection(date);
    }

    private changeSelection(selected: IsoDate) {
        this.viewStore.updateCurrentDate(selected);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
